/**
 * KYC domain — TanStack Query keys and fetchers.
 */
import { apiFetch } from '../../lib/api/client.js';

export const KYC_QUERY_KEYS = {
  status: ['kyc', 'status'] as const,
} as const;

export type KycStatus = 'unsubmitted' | 'pending' | 'approved' | 'rejected';
export type KycDocumentType = 'id_card' | 'address_proof' | 'selfie' | 'bank_statement';
export type KycDocumentStatus = 'uploaded' | 'approved' | 'rejected';

export interface KycDocument {
  id: string;
  memberId: string;
  type: KycDocumentType;
  filename: string;
  mimeType: string;
  sizeBytes: number;
  status: KycDocumentStatus;
  rejectionReason: string | null;
  uploadedAt: string;
  createdAt: string;
  updatedAt: string;
}

export interface KycStatusResponse {
  status: KycStatus;
  documents: KycDocument[];
  rejectionReason?: string | null;
}

export function fetchKycStatus(): Promise<KycStatusResponse> {
  return apiFetch<KycStatusResponse>('/members/me/kyc');
}

export interface UploadKycDocumentArgs {
  file: File;
  type: KycDocumentType;
}

// `/kyc-documents` is its own gateway resource (apps/api-gateway/src/upstreams.ts),
// not a sub-path of `/members` — it carries the upload-sized body cap and
// rate-limit tier the generic member write policy does not.
export async function uploadKycDocument(args: UploadKycDocumentArgs): Promise<KycDocument> {
  const form = new FormData();
  form.append('document', args.file);
  form.append('type', args.type);
  return apiFetch<KycDocument>('/kyc-documents', {
    method: 'POST',
    body: form,
    // Do NOT set content-type — browser sets it with the boundary automatically.
  });
}

export async function deleteKycDocument(docId: string): Promise<KycDocument> {
  return apiFetch<KycDocument>(`/kyc-documents/${docId}`, {
    method: 'DELETE',
  });
}

// ── Derived helpers ────────────────────────────────────────────────────────────

export const KYC_DOCUMENT_TYPES: KycDocumentType[] = [
  'id_card',
  'address_proof',
  'selfie',
  'bank_statement',
];

/** Whether the given MIME type can be previewed as an image. */
export function isImageMime(mime: string): boolean {
  return mime === 'image/jpeg' || mime === 'image/png';
}
